import React, { useState, useEffect, useCallback } from 'react';
import {
    Card, Table, Tag, Input, Select, Row, Col, Statistic, Space, Typography, Grid, Popconfirm, Button, message
} from 'antd';
import {
    TeamOutlined, UserOutlined, SearchOutlined, CrownOutlined,
    CustomerServiceOutlined, ShopOutlined, CarOutlined, DeleteOutlined, PhoneOutlined
} from '@ant-design/icons';
import { allUsersService, citiesService } from '../../common/services';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

const ROLE_CONFIG = {
    chauffeur: { label: 'Chauffeur', color: 'blue', icon: <CarOutlined /> },
    commercial: { label: 'Affilié', color: 'orange', icon: <ShopOutlined /> },
    admin: { label: 'Admin', color: 'purple', icon: <UserOutlined /> },
    superAdmin: { label: 'Super Admin', color: 'red', icon: <CrownOutlined /> },
    directeur: { label: 'Directeur', color: 'gold', icon: <CrownOutlined /> },
    support: { label: 'Support', color: 'cyan', icon: <CustomerServiceOutlined /> },
    comptable: { label: 'Comptable', color: 'green', icon: <UserOutlined /> },
};

const AllUsersPage = () => {
    const [loading, setLoading] = useState(false);
    const [users, setUsers] = useState([]);
    const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0 });
    const [stats, setStats] = useState({});
    const [search, setSearch] = useState('');
    const [roleFilter, setRoleFilter] = useState(null);
    const [cityFilter, setCityFilter] = useState(null);
    const [cities, setCities] = useState([]);

    const screens = Grid.useBreakpoint();
    const [deleting, setDeleting] = useState(null);
    const [normalizing, setNormalizing] = useState(false);

    // Charger les villes
    useEffect(() => {
        citiesService.getAll().then(res => {
            if (res?.data) setCities(res.data.filter(c => c.isActive !== false));
        }).catch(() => {});
    }, []);

    // Charger les utilisateurs
    const fetchUsers = useCallback(async (page = 1) => {
        setLoading(true);
        try {
            const params = {
                page,
                limit: pagination.limit,
                sort: '-createdAt',
            };
            if (search) params.search = search;
            if (roleFilter) params.role = roleFilter;
            if (cityFilter) params.city = cityFilter;

            const res = await allUsersService.getAll(params);
            if (res?.success) {
                setUsers(res.data || []);
                setPagination(prev => ({
                    ...prev,
                    page: res.pagination.page,
                    total: res.pagination.total,
                }));
                setStats(res.stats || {});
            }
        } catch (error) {
            console.error('Erreur chargement utilisateurs:', error);
        } finally {
            setLoading(false);
        }
    }, [search, roleFilter, cityFilter, pagination.limit]);

    useEffect(() => {
        fetchUsers(1);
    }, [search, roleFilter, cityFilter]);

    // Déterminer les rôles affichés (peut avoir plusieurs rôles)
    const getUserRoles = (record) => {
        const roles = [];
        // Rôle principal
        const baseRole = ROLE_CONFIG[record.role] || { label: record.role, color: 'default', icon: <UserOutlined /> };
        roles.push(baseRole);
        // Affilié (en plus du rôle principal si chauffeur converti)
        if (record.affiliateProfile?.status && record.role !== 'commercial') {
            roles.push(ROLE_CONFIG.commercial);
        }
        // Ambassadeur
        if (record.ambassadorProfile?.status) {
            roles.push({ label: 'Ambassadeur', color: 'volcano', icon: <CrownOutlined /> });
        }
        return roles;
    };

    // Supprimer un utilisateur
    const handleDelete = async (userId) => {
        setDeleting(userId);
        try {
            const res = await allUsersService.delete(userId);
            if (res?.success) {
                message.success('Utilisateur supprimé avec succès');
                fetchUsers(pagination.page);
            } else {
                message.error(res?.message || 'Erreur lors de la suppression');
            }
        } catch (error) {
            message.error(error?.message || 'Erreur lors de la suppression');
        } finally {
            setDeleting(null);
        }
    };

    // Normaliser les numéros de téléphone
    const handleNormalizePhones = async () => {
        setNormalizing(true);
        try {
            const res = await allUsersService.normalizePhones();
            if (res?.success) {
                message.success(res.message || 'Numéros normalisés avec succès');
                fetchUsers(1);
            } else {
                message.error(res?.message || 'Erreur lors de la normalisation');
            }
        } catch (error) {
            message.error(error?.message || 'Erreur lors de la normalisation');
        } finally {
            setNormalizing(false);
        }
    };

    const columns = [
        {
            title: 'Utilisateur',
            key: 'user',
            render: (_, record) => (
                <Space>
                    <UserOutlined />
                    <div>
                        <div style={{ fontWeight: 500 }}>{record.firstName} {record.lastName}</div>
                        <Text type="secondary" style={{ fontSize: 12 }}>{record.phone}</Text>
                    </div>
                </Space>
            ),
        },
        {
            title: 'Rôle',
            key: 'role',
            render: (_, record) => {
                const roles = getUserRoles(record);
                return (
                    <Space size={4} wrap>
                        {roles.map((r, i) => (
                            <Tag key={i} color={r.color} icon={r.icon}>
                                {r.label}
                            </Tag>
                        ))}
                    </Space>
                );
            },
        },
        {
            title: 'Ville',
            key: 'city',
            render: (_, record) => record.city?.name || '-',
        },
        {
            title: 'Affilié',
            key: 'affiliate',
            render: (_, record) => {
                const affiliate = record.referredBy?.affiliateId;
                if (affiliate && typeof affiliate === 'object') {
                    return (
                        <div>
                            <div style={{ fontWeight: 500, fontSize: 12 }}>{affiliate.firstName} {affiliate.lastName}</div>
                            <Text type="secondary" style={{ fontSize: 11 }}>{affiliate.phone}</Text>
                        </div>
                    );
                }
                if (record.referredBy?.referralCode) {
                    return (
                        <Popconfirm
                            title="Délier ce chauffeur ?"
                            description="L'affilié lié a été supprimé. Voulez-vous nettoyer cette référence ?"
                            onConfirm={async () => {
                                try {
                                    const res = await allUsersService.unlinkOrphan(record._id);
                                    if (res?.success) {
                                        message.success('Référence nettoyée');
                                        fetchUsers(pagination.page);
                                    } else {
                                        message.error(res?.message || 'Erreur');
                                    }
                                } catch (error) {
                                    message.error(error?.message || 'Erreur');
                                }
                            }}
                            okText="Oui, délier"
                            cancelText="Annuler"
                        >
                            <Tag color="red" style={{ cursor: 'pointer' }}>Affilié supprimé</Tag>
                        </Popconfirm>
                    );
                }
                return <Text type="secondary">-</Text>;
            },
        },
        {
            title: 'Date inscription',
            key: 'createdAt',
            render: (_, record) => (
                <span style={{ fontSize: 12 }}>
                    {dayjs(record.createdAt).format('DD/MM/YYYY HH:mm')}
                </span>
            ),
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 80,
            render: (_, record) => (
                <Popconfirm
                    title="Supprimer cet utilisateur ?"
                    description={`${record.firstName} ${record.lastName} (${record.phone})`}
                    onConfirm={() => handleDelete(record._id)}
                    okText="Oui, supprimer"
                    cancelText="Annuler"
                    okButtonProps={{ danger: true }}
                >
                    <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        size="small"
                        loading={deleting === record._id}
                    />
                </Popconfirm>
            ),
        },
    ];

    const totalUsers = Object.values(stats).reduce((a, b) => a + b, 0);

    return (
        <div>
            {/* Stats */}
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                <Col xs={12} sm={8} md={4}>
                    <Card size="small">
                        <Statistic
                            title="Total"
                            value={totalUsers}
                            prefix={<TeamOutlined />}
                            valueStyle={{ fontSize: 20 }}
                        />
                    </Card>
                </Col>
                {Object.entries(ROLE_CONFIG).map(([key, config]) => (
                    stats[key] ? (
                        <Col xs={12} sm={8} md={4} key={key}>
                            <Card
                                size="small"
                                style={{ cursor: 'pointer', borderColor: roleFilter === key ? config.color : undefined }}
                                onClick={() => setRoleFilter(roleFilter === key ? null : key)}
                            >
                                <Statistic
                                    title={config.label}
                                    value={stats[key] || 0}
                                    prefix={config.icon}
                                    valueStyle={{ fontSize: 20, color: roleFilter === key ? config.color : undefined }}
                                />
                            </Card>
                        </Col>
                    ) : null
                ))}
            </Row>

            {/* Filtres */}
            <Card style={{ marginBottom: 16 }}>
                <Row gutter={[16, 16]} align="middle">
                    <Col xs={24} sm={9}>
                        <Input
                            placeholder="Rechercher par nom ou téléphone..."
                            prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            allowClear
                            size="large"
                        />
                    </Col>
                    <Col xs={12} sm={6}>
                        <Select
                            placeholder="Filtrer par rôle"
                            value={roleFilter}
                            onChange={v => setRoleFilter(v)}
                            allowClear
                            style={{ width: '100%' }}
                            size="large"
                        >
                            {Object.entries(ROLE_CONFIG).map(([key, config]) => (
                                <Select.Option key={key} value={key}>
                                    {config.label}
                                </Select.Option>
                            ))}
                        </Select>
                    </Col>
                    <Col xs={12} sm={6}>
                        <Select
                            placeholder="Filtrer par ville"
                            value={cityFilter}
                            onChange={v => setCityFilter(v)}
                            allowClear
                            style={{ width: '100%' }}
                            size="large"
                        >
                            {cities.map(city => (
                                <Select.Option key={city._id} value={city._id}>
                                    {city.name}
                                </Select.Option>
                            ))}
                        </Select>
                    </Col>
                    <Col xs={24} sm={3}>
                        <Popconfirm
                            title="Normaliser les numéros ?"
                            description="Ajouter +225 aux numéros qui ne l'ont pas et supprimer les doublons"
                            onConfirm={handleNormalizePhones}
                            okText="Oui, normaliser"
                            cancelText="Annuler"
                        >
                            <Button
                                icon={<PhoneOutlined />}
                                loading={normalizing}
                                size="large"
                                style={{ width: '100%' }}
                            >
                                Corriger +225
                            </Button>
                        </Popconfirm>
                    </Col>
                </Row>
            </Card>

            {/* Table */}
            <Card>
                <Table
                    columns={columns}
                    dataSource={users}
                    rowKey="_id"
                    loading={loading}
                    pagination={{
                        current: pagination.page,
                        pageSize: pagination.limit,
                        total: pagination.total,
                        showSizeChanger: false,
                        showTotal: (total) => `${total} utilisateur${total > 1 ? 's' : ''}`,
                        onChange: (page) => fetchUsers(page),
                    }}
                    locale={{ emptyText: 'Aucun utilisateur trouvé' }}
                    scroll={{ x: 800 }}
                />
            </Card>
        </div>
    );
};

export default AllUsersPage;
